import * as THREE from "three";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";
import { inked, toon } from "../toon";
import type { RiderBuild } from "../styles";
import { buildNaturalBoot, buildNaturalMoonBoot, buildNaturalShoe } from "./feet-natural";
import type { SkaterLook } from "./look";
import { cloth } from "./materials";
import { facets } from "./geom";

// Footwear (toe toward -x, like the rider's chest): skate shoes, western boots, moon boots.
// Each build's footwear kind (`RiderBuild.feet`) is one entry in `FEET`; the
// natural kind's are `feet-natural.ts`.

export type ShoeStyle = NonNullable<SkaterLook["shoeStyle"]>;

export const FEET: Readonly<Record<RiderBuild["feet"], Readonly<Record<ShoeStyle, (color: string) => THREE.Group>>>> = {
  toy: { skate: buildShoe, boots: buildBoot, moon: buildMoonBoot },
  natural: { skate: buildNaturalShoe, boots: buildNaturalBoot, moon: buildNaturalMoonBoot },
};

/**
 * A skate shoe (toe toward -x, like the rider's chest): a gum sole, a puffy
 * upper with a rounded toe cap, a padded collar and a row of laces.
 */
export function buildShoe(color: string): THREE.Group {
  const shoe = new THREE.Group();
  shoe.name = "shoe";
  const sole = inked(new RoundedBoxGeometry(0.27, 0.035, 0.125, facets(2, 1), 0.015), toon("#3a3346", { roughness: 0.8 }), 0.01);
  sole.position.y = -0.03;
  shoe.add(sole);
  const upperMat = cloth(color, false, { roughness: 0.7 });
  const upper = inked(new RoundedBoxGeometry(0.2, 0.075, 0.115, facets(3, 1), 0.03), upperMat, 0.012);
  upper.position.set(0.02, 0.01, 0);
  shoe.add(upper);
  const toe = new THREE.Mesh(new THREE.SphereGeometry(0.058, facets(16), facets(10, 2)), upperMat);
  toe.scale.set(1.25, 0.62, 1);
  toe.position.set(-0.07, -0.003, 0);
  toe.castShadow = true;
  shoe.add(toe);
  const collar = new THREE.Mesh(new THREE.TorusGeometry(0.045, 0.014, facets(8, 2), facets(16)), toon("#3a3346", { roughness: 0.8 }));
  collar.rotation.x = Math.PI / 2;
  collar.position.set(0.06, 0.05, 0);
  shoe.add(collar);
  const laceMat = toon("#e94b8a", { roughness: 0.6 });
  for (let i = 0; i < 3; i++) {
    const lace = new THREE.Mesh(new THREE.CapsuleGeometry(0.008, 0.06, facets(2, 1), facets(6)), laceMat);
    lace.rotation.x = Math.PI / 2;
    lace.position.set(-0.03 + i * 0.03, 0.05, 0);
    shoe.add(lace);
  }
  return shoe;
}

/** A western boot (toe toward -x): a pointed toe, a stacked heel and a shaft up the shin. */
export function buildBoot(color: string): THREE.Group {
  const boot = new THREE.Group();
  boot.name = "shoe";
  const sole = inked(new RoundedBoxGeometry(0.28, 0.03, 0.115, facets(2, 1), 0.012), toon("#3a2418", { roughness: 0.8 }), 0.01);
  sole.position.y = -0.032;
  boot.add(sole);
  const leather = toon(color, { roughness: 0.55 }, "leather");
  const upper = inked(new RoundedBoxGeometry(0.22, 0.07, 0.105, facets(3, 1), 0.03), leather, 0.012);
  upper.position.set(0.01, 0.008, 0);
  boot.add(upper);
  // A narrow, slightly pointed toe.
  const toe = new THREE.Mesh(new THREE.ConeGeometry(0.05, 0.09, facets(12)), leather);
  toe.rotation.z = Math.PI / 2;
  toe.scale.set(1, 1, 0.6);
  toe.position.set(-0.12, -0.005, 0);
  toe.castShadow = true;
  boot.add(toe);
  const heel = inked(new THREE.BoxGeometry(0.05, 0.035, 0.08), toon("#3a2418", { roughness: 0.8 }), 0.008);
  heel.position.set(0.1, -0.055, 0);
  boot.add(heel);
  const shaftGeo = new THREE.CylinderGeometry(0.085, 0.07, 0.16, facets(14), 1, true);
  shaftGeo.translate(0, 0.1, 0);
  const shaft = inked(shaftGeo, toon(color, { roughness: 0.55, side: THREE.DoubleSide }, "leather"), 0.012);
  shaft.position.x = 0.05;
  boot.add(shaft);
  // Stitching up the shaft.
  const stitch = new THREE.Mesh(new THREE.TorusGeometry(0.08, 0.008, facets(6, 2), facets(16)), toon("#e8c77a", { roughness: 0.6 }));
  stitch.rotation.x = Math.PI / 2;
  stitch.position.set(0.05, 0.17, 0);
  boot.add(stitch);
  return boot;
}

/** A chunky moon boot (toe toward -x): a thick tread and a puffy, padded upper. */
export function buildMoonBoot(color: string): THREE.Group {
  const boot = new THREE.Group();
  boot.name = "shoe";
  const sole = inked(new RoundedBoxGeometry(0.3, 0.05, 0.15, facets(2, 1), 0.02), toon("#5b6270", { roughness: 0.85 }), 0.01);
  sole.position.y = -0.028;
  boot.add(sole);
  const padded = cloth(color, false, { roughness: 0.75 });
  const upper = inked(new RoundedBoxGeometry(0.26, 0.1, 0.145, facets(3, 1), 0.045), padded, 0.012);
  upper.position.set(0.005, 0.035, 0);
  boot.add(upper);
  const cuff = inked(new THREE.CylinderGeometry(0.09, 0.095, 0.1, facets(14)), padded, 0.012);
  cuff.position.set(0.05, 0.11, 0);
  boot.add(cuff);
  const strap = new THREE.Mesh(new RoundedBoxGeometry(0.03, 0.11, 0.15, facets(2, 1), 0.01), toon("#5b6270", { roughness: 0.6 }));
  strap.position.set(-0.04, 0.04, 0);
  boot.add(strap);
  return boot;
}
